import { useEffect, useMemo } from 'react';

import {
  buildPageMeta,
  serializeJsonLdBlock,
  HEAD_SSR_ATTR,
  type PageMetaInput,
} from './page-meta';
import { useSeoCollector } from './seo-collector';
import { useDocumentTitle } from './use-document-title';

/**
 * Per-route SEO `<head>` injection hook (AF-136).
 *
 * Consumes the pure `buildPageMeta` generator (iter 65) and applies its output
 * to the document head: the `<title>` (via the existing `useDocumentTitle`), a
 * `<meta name="description">`, the Open Graph + Twitter Card `<meta>` tags, a
 * `<link rel="canonical">`, and the schema.org JSON-LD `<script>`. The marketing
 * SPA had no per-page meta beyond the title; this is the capability that makes
 * the generator actually reach search engines + social cards.
 *
 * **Managed-tag discipline** (Lead Dev hat): every tag this hook injects carries
 * a `data-seo-managed` marker and is removed on cleanup, so route changes never
 * leak a stale description / OG card into the next route (the same restore-on-
 * unmount contract `useDocumentTitle` uses for the title). The static
 * `index.html` head is never touched.
 *
 * **Stable recompute**: the meta is `useMemo`'d over the input fields so the
 * injection effect only re-runs when the page's SEO inputs actually change, not
 * on every render.
 *
 * Per-page wiring (calling this from each route component with its title +
 * description) is the adoption step that follows.
 */

const MANAGED_ATTR = 'data-seo-managed';

export function useSeoMeta(input: PageMetaInput): void {
  const {
    title,
    description,
    path,
    fullTitle,
    type,
    imageUrl,
    publishedTime,
    faq,
    locale,
    alternates,
    breadcrumbLabels,
  } = input;
  const meta = useMemo(
    () =>
      // Spread optionals only when defined — `exactOptionalPropertyTypes` rejects
      // passing an optional field as an explicit `undefined`.
      buildPageMeta({
        title,
        description,
        path,
        ...(fullTitle !== undefined ? { fullTitle } : {}),
        ...(type !== undefined ? { type } : {}),
        ...(imageUrl !== undefined ? { imageUrl } : {}),
        ...(publishedTime !== undefined ? { publishedTime } : {}),
        ...(faq !== undefined ? { faq } : {}),
        ...(locale !== undefined ? { locale } : {}),
        ...(alternates !== undefined ? { alternates } : {}),
        ...(breadcrumbLabels !== undefined ? { breadcrumbLabels } : {}),
      }),
    [
      title,
      description,
      path,
      fullTitle,
      type,
      imageUrl,
      publishedTime,
      faq,
      locale,
      alternates,
      breadcrumbLabels,
    ],
  );

  useDocumentTitle(meta.title);

  // SSG: when rendered under the prerender's collector (server only), hand the
  // computed meta back so the build can bake it into the static `<head>`. No-op
  // on the client (the collector context is never provided there).
  const collect = useSeoCollector();
  if (collect) {
    collect(meta);
  }

  useEffect(() => {
    const head = document.head;
    // Hydration handoff: drop the prerendered SSG baseline (marked with
    // HEAD_SSR_ATTR) exactly once before injecting the client-managed tags, so
    // the first paint never carries a duplicate description / canonical /
    // JSON-LD. After the first mount no such tags exist, so this is a no-op.
    for (const stale of head.querySelectorAll(`[${HEAD_SSR_ATTR}]`)) {
      stale.remove();
    }
    const created: Element[] = [];

    for (const tag of meta.metaTags) {
      const el = document.createElement('meta');
      if (tag.name !== undefined) {
        el.setAttribute('name', tag.name);
      }
      if (tag.property !== undefined) {
        el.setAttribute('property', tag.property);
      }
      el.setAttribute('content', tag.content);
      el.setAttribute(MANAGED_ATTR, '');
      head.appendChild(el);
      created.push(el);
    }

    const canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', meta.canonicalUrl);
    canonical.setAttribute(MANAGED_ATTR, '');
    head.appendChild(canonical);
    created.push(canonical);

    for (const alt of meta.alternateLinks) {
      const el = document.createElement('link');
      el.setAttribute('rel', 'alternate');
      el.setAttribute('hreflang', alt.hreflang);
      el.setAttribute('href', alt.href);
      el.setAttribute(MANAGED_ATTR, '');
      head.appendChild(el);
      created.push(el);
    }

    for (const block of meta.jsonLdBlocks) {
      const jsonLd = document.createElement('script');
      jsonLd.setAttribute('type', 'application/ld+json');
      jsonLd.setAttribute(MANAGED_ATTR, '');
      jsonLd.textContent = serializeJsonLdBlock(block);
      head.appendChild(jsonLd);
      created.push(jsonLd);
    }

    return () => {
      for (const el of created) {
        el.remove();
      }
    };
  }, [meta]);
}
